/**
 * Content for the signed-out Discover landing — the marketing surface a guest
 * sees in place of the (auth-gated) catalog, giving them a reason to sign in.
 *
 * Single source of truth for the copy + counts (shared-string rule). The figures
 * mirror docs/marketing/marketing_one_pager.html and are rounded (down) from the
 * shipped catalog (seed-output/manifest.v1.json: 2,107 activities; ~4,000 videos,
 * 1,900+ with male & female models, 38 muscles, 25 equipment types) — update HERE
 * on any reseed, then keep the one-pager and landing copy in step (see the
 * marketing/llms copy-drift note).
 *
 * Only the EXERCISE library is advertised with numbers. The ready-made circuits,
 * workouts and programs built on top of it are AI-authored general templates that
 * account for nobody's body, so counting them reads as a claim about depth of
 * programming we can't stand behind. They're described as a starting point the
 * user then customises, and never quantified — here, on the marketing site, or in
 * the docs. Keep it that way.
 *
 * The catalog is **3D anatomy animation, not footage** — never describe it as
 * "filmed". The models are rendered, and the muscles a move works are
 * highlighted on them, which is the actual differentiator and impossible to
 * shoot on camera. Say "animated" / "3D"; "model" is still correct (it's a 3D
 * model), as is "video" (they are video clips).
 */

/** A single number + caption tile in the stat strip. */
export interface GuestStat {
  /** Display value, already formatted (e.g. "4,000", "78%"). */
  value: string;
  /** Short uppercase caption under the number. */
  label: string;
}

/** A "way to move" category card. */
export interface GuestCategory {
  /** Rounded video count for the category (e.g. "3,000+"). */
  count: string;
  title: string;
  blurb: string;
}

export const DISCOVER_GUEST_HERO = {
  eyebrow: 'Gym · Yoga · Home · Mobility',
  titleLead: 'The complete library of ',
  titleAccent: 'human movement.',
  sub: '2,100+ exercises as clear 3D anatomy animations — you see exactly which muscles each move works, most shown with both a male and a female model. One library for strength, yoga and cardio, with ready-made plans to start from and a full builder to make them your own.',
} as const;

export const DISCOVER_GUEST_STATS: readonly GuestStat[] = [
  { value: '4,000', label: 'HD movement videos' },
  { value: '2,100+', label: 'Distinct exercises' },
  { value: '1,900+', label: 'Shown male & female' },
  { value: '78%', label: 'Doable at home' },
  { value: '38', label: 'Muscles targeted' },
  { value: '25', label: 'Equipment types' },
];

export const DISCOVER_GUEST_CATEGORIES: readonly GuestCategory[] = [
  {
    count: '3,000+',
    title: 'Strength & Toning',
    blurb: 'Barbell, dumbbell, cable, kettlebell, machine and bodyweight work for every muscle group.',
  },
  {
    count: '500+',
    title: 'Yoga & Stretching',
    blurb: 'Foundational poses to full mobility routines — from Sun Salutation to deep recovery stretches.',
  },
  {
    count: '450+',
    title: 'Cardio & Plyometrics',
    blurb: 'High-energy, explosive movements for conditioning, fat-burn and HIIT circuits.',
  },
];

/** The ready-made section — deliberately unquantified (see the file header).
 *  It says what the plans are for and what they are not, then hands over to the
 *  builder line below. */
export const DISCOVER_GUEST_PLANS = {
  titleLead: 'A ',
  titleAccent: 'starting point',
  titleTail: ' you can adapt',
  sub: 'Discover ships with ready-made circuits, workouts and multi-day programs you can add in one tap, alongside sequences and programs published by other members. They were created using AI or by another user, are general information only, and are not personalised to you — so start from one, take all precautions, and change the exercises, sets, reps and rest until it suits you.',
} as const;

/** Closing "build your own" line under the ready-made plans. */
export const DISCOVER_GUEST_BUILD =
  '…Or drag any of 2,100+ movements — and your own photos and video — into your own workouts, yoga flows and multi-day programs.';

export const DISCOVER_GUEST_CTA = {
  eyebrow: '2,100+ exercises · 4,000 videos · one library',
  titleLead: 'Every workout. Every body. ',
  titleAccent: 'One library.',
  sub: 'Create a free account to browse the full catalog, start from a ready-made plan, and build your own workouts and flows.',
} as const;
